import React from 'react';
import type { Period, RestraintRows, ReviewRow } from '../../dashboard/types';
import {
  APPOINTMENT_REASONS,
  TRAIN_ELVA,
  heldByCapLink,
  optedOutLink,
  reviewRowLink,
} from '../../dashboard/links';
import { DashCard, DashFooterStrip, DashRowLink, Metric, count } from './primitives';

/**
 * Waiting for a human to teach it (spec 17 §5.6). **`RIGHT NOW`.**
 *
 * The four rows sum to the Train ELVA nav badge — **same source, one number**
 * (§7 identity 6).
 *
 * The empty state here is the best news on the page, and it is written as such:
 * *Nothing waiting. ELVA answered everything it was asked.* **An empty queue is an
 * achievement; an empty chart is an absence** (P346). Rendering both as a sad grey
 * nought throws the good news away.
 */
export function ReviewQueueCard({ rows }: { rows: ReviewRow[]; badge?: number }) {
  const total = rows.reduce((n, r) => n + r.count, 0);

  if (total === 0) {
    return (
      <DashCard title="Waiting for a human to teach it" rightNow>
        <div className="px-6 pb-[19px]">
          <p className="m-0 text-[13px] font-semibold leading-[1.6] text-[var(--money-green)]">
            Nothing waiting. ELVA answered everything it was asked.
          </p>
        </div>
      </DashCard>
    );
  }

  return (
    <DashCard
      title="Waiting for a human to teach it"
      rightNow
      sub={<>The Train ELVA queue, broken down.</>}
    >
      <div>
        {rows.map((r) => (
          <DashRowLink key={r.label} href={reviewRowLink(r.queue)}>
            <div
              className="flex items-center justify-between gap-4 px-6 py-[11px]"
              style={{ borderBottom: '1px dotted #DFE3ED' }}
            >
              <span className="min-w-0 truncate text-[12.5px] text-[var(--text-secondary)]">{r.label}</span>
              <span className="shrink-0 font-[var(--font-mono)] text-[13px] font-bold text-[var(--text-primary)]">
                {count(r.count)}
              </span>
            </div>
          </DashRowLink>
        ))}
      </div>
      <DashFooterStrip tone="indigo">
        <span className="flex flex-wrap items-center justify-between gap-3">
          <span>
            This is <b className="font-semibold text-[var(--text-primary)]">work to do</b>, not a report. The dashboard
            only shows how big it is.
          </span>
          <a href={TRAIN_ELVA} className="shrink-0 font-semibold text-[var(--brand-indigo)] hover:underline">
            Open Train ELVA →
          </a>
        </span>
      </DashFooterStrip>
    </DashCard>
  );
}

/**
 * Restraint and coverage (§5.7).
 *
 * **This is the only place in the product where what ELVA declined to do is
 * visible** (P335). Every other surface reports what it did. It is also what keeps
 * the 10DLC registration alive, so do not quietly drop it when space is tight.
 *
 * The first two rows are period-scoped; **the third is always now**, because a
 * reason with no role mapped cannot be booked at all, today, whatever period you
 * are looking at.
 */
export function RestraintCard({ rows, period }: { rows: RestraintRows; period: Period }) {
  const optOutPct =
    rows.outboundTotal > 0 ? ((rows.optedOut / rows.outboundTotal) * 100).toFixed(1) : undefined;

  return (
    <DashCard
      title="Restraint and coverage"
      sub={
        <>
          What ELVA <b className="font-semibold text-[var(--text-secondary)]">chose not to do</b>, and where it has no
          instructions. The only page where either is visible.
        </>
      }
    >
      <Row
        label="Opted out"
        desc={
          rows.optedOut === 0 ? (
            'Nobody asked to be left alone during this period.'
          ) : (
            <>
              {count(rows.optedOut)} {rows.optedOut === 1 ? 'person' : 'people'} left of {count(rows.outboundTotal)}{' '}
              outbound messages{optOutPct !== undefined ? ` · ${optOutPct}%` : ''}
            </>
          )
        }
        href={optedOutLink(period)}
        value={
          <span
            className="font-[var(--font-mono)] text-[15px] font-bold"
            style={{ color: rows.optedOut === 0 ? 'var(--money-green)' : 'var(--text-primary)' }}
          >
            {/* A minus, because this is a subtraction from what ELVA may do. */}
            {rows.optedOut === 0 ? '0' : `−${count(rows.optedOut)}`}
          </span>
        }
      />
      <Row
        label="Messages held back to avoid contacting someone too often"
        desc="ELVA decided these would have been one message too many"
        href={heldByCapLink(period)}
        value={<Metric value={rows.heldByCap} size={15} className="font-[var(--font-mono)]" />}
      />
      {/* RIGHT NOW: not scoped, and amber when non-zero. */}
      <Row
        last
        label="Appointment reasons with nothing behind them"
        desc="No role is mapped, so ELVA cannot book them at all"
        href={APPOINTMENT_REASONS}
        rightNow
        value={
          <span
            className="font-[var(--font-mono)] text-[15px] font-bold"
            style={{ color: rows.uncoveredReasons === 0 ? 'var(--money-green)' : 'var(--warn-amber)' }}
          >
            {count(rows.uncoveredReasons)}
          </span>
        }
      />
    </DashCard>
  );
}

function Row({
  label,
  desc,
  value,
  href,
  last,
  rightNow,
}: {
  label: string;
  desc: React.ReactNode;
  value: React.ReactNode;
  href: string;
  last?: boolean;
  rightNow?: boolean;
}) {
  return (
    <DashRowLink href={href}>
      <div
        className={last === true ? 'flex items-start justify-between gap-4 px-6 pb-[19px] pt-[13px]' : 'flex items-start justify-between gap-4 px-6 py-[13px]'}
        style={last === true ? undefined : { borderBottom: '1px dotted #DFE3ED' }}
      >
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">{label}</span>
            {rightNow === true ? (
              <span className="rounded-full bg-[var(--brand-indigo-tint)] px-1.5 py-[2px] text-[9px] font-bold uppercase tracking-[0.06em] text-[var(--brand-indigo)]">
                Right now
              </span>
            ) : null}
          </span>
          <span className="mt-0.5 block text-[11.5px] leading-[1.5] text-[var(--text-muted)]">{desc}</span>
        </span>
        <span className="shrink-0">{value}</span>
      </div>
    </DashRowLink>
  );
}

/**
 * The two rules (§5.8). Shipped verbatim from the board, because **both are rules
 * a reader has to know to read the page correctly** — not commentary.
 */
export function TwoRulesCard() {
  return (
    <section
      className="overflow-hidden rounded-[18px] border border-[#E7EAF2] bg-[var(--surface-card)]"
      style={{ boxShadow: '0 1px 3px rgba(36,41,54,0.05)' }}
    >
      <div className="px-6 py-[17px] text-[12.5px] leading-[1.7] text-[var(--text-secondary)]">
        <b className="font-semibold text-[var(--text-primary)]">Two clocks on one page, and they are labelled.</b> The
        tiles, the chart and the restraint figures are{' '}
        <b className="font-semibold text-[var(--text-primary)]">scoped to the period</b> in the picker. The schedule, the
        review queue and the uncovered reasons carry a{' '}
        <b className="font-semibold text-[var(--text-primary)]">Right now</b> tag, because they are true at this second
        and do not change when the period does —{' '}
        <b className="font-semibold text-[var(--text-primary)]">
          &ldquo;3 people are still waiting&rdquo; means the same thing whether you are looking at a day or a month.
        </b>
      </div>
      <div
        className="px-6 py-[17px] text-[12.5px] leading-[1.7]"
        style={{ background: '#FBF3E4', color: 'var(--warn-amber)', borderTop: '1px solid #F0E0BE' }}
      >
        <b className="font-semibold">A quiet row is three different situations.</b> An agent with an empty queue, an
        agent somebody paused, and an agent whose steps stopped halfway all render as a calm line unless we make them
        different — which is why <b className="font-semibold">Blocked</b> is its own state, and why the last column says
        what is stuck rather than leaving it to be noticed.
      </div>
    </section>
  );
}
